import {
  useQpmRequirementGetQuery,
  useQpmRequirementListQuery,
} from "@/services/backend/qpm/requirements/api/requirement-api";
import { RTKQueryErrorAlert } from "@/shared/components/errors/rtk-query-error-alert";
import { Skeleton } from "@/shared/components/ui/skeleton";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/shared/components/ui/card";
import t from "@/lang/lang";
import {
  RequirementList,
  RequirementListDTO,
} from "@/services/backend/qpm/requirements/types/requirement";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/shared/components/ui/select";
import { formatDate, formatTime } from "@/shared/lib/utilities/date";
import { useQpmCalculationLoadRequirementMutation } from "@/services/backend/qpm/calculations/api/calculation-api";
import {
  Calculation,
  LoadRequirementRequest,
} from "@/services/backend/qpm/calculations/types/calculation";
import { DocumentNumberById } from "@/services/backend/numbers/components/document-number";
import { Loader2 } from "lucide-react";
import { ReactNode } from "react";
import { Button } from "@/shared/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogTitle,
  DialogTrigger,
} from "@/shared/components/ui/dialog";
import { CreateFromRequirement } from "@/routes/processes/[processId]/qpm/calculations/_components/create-from-requirement";
import { RequirementDTO } from "@/services/backend/qpm/requirements/types/requirement-dto";

export type SelectRequirementComponentProps = {
  calculation: Calculation;
  customerId: string;
  processId: string;
};

export function SelectRequirementComponent({
  calculation,
  customerId,
  processId,
}: SelectRequirementComponentProps) {
  const {
    data: list,
    isLoading,
    error,
  } = useQpmRequirementListQuery({
    process: {
      active: true,
      values: [processId!],
    },
    customer: {
      active: true,
      values: [customerId],
    },
  });

  const {
    data: selectedRequirement,
    isLoading: isLoadingSelectedRequirement,
    error: errorSelectedRequirement,
  } = useQpmRequirementGetQuery(
    { id: calculation.boundRequirementId! },
    { skip: !calculation.boundRequirementId },
  );

  if (error) {
    return <RTKQueryErrorAlert error={error} />;
  }

  if (errorSelectedRequirement) {
    return <RTKQueryErrorAlert error={errorSelectedRequirement} />;
  }

  if (isLoading || isLoadingSelectedRequirement) {
    return (
      <div className="mb-2">
        <Skeleton className="h-32 w-full" />
      </div>
    );
  }

  return (
    <div className="mb-2">
      <Card>
        <CardHeader>
          <CardTitle>{t("Bestandsaufnahmen")}</CardTitle>
          <CardDescription>
            {t(
              "Wählen Sie hier die Bestandsaufnahme, auf Basis derer Sie die Kalkulation erstellen möchten.",
            )}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="w-full">
            <RequirementSelector
              calculationId={calculation.id}
              requirementList={list!}
              selectedRequirement={selectedRequirement}
            />
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

type RequirementDropDownProps = {
  calculationId: string;
  requirementList: RequirementList;
  selectedRequirement?: RequirementDTO;
};

function RequirementSelector({
  calculationId,
  requirementList,
  selectedRequirement,
}: RequirementDropDownProps) {
  const [loadRequirementData, { error, isLoading }] =
    useQpmCalculationLoadRequirementMutation();

  const onChange = (value: string) => {
    const requirement = requirementList.items.find((req) => req.id === value);
    if (!requirement || isLoading || error) {
      return;
    }
    const request: LoadRequirementRequest = {
      calculationId,
      requirementId: requirement.id,
    };

    loadRequirementData(request);
  };

  return (
    <Select
      defaultValue={selectedRequirement?.id}
      value={selectedRequirement?.id}
      onValueChange={(value: string) => {
        onChange(value);
      }}
    >
      <SelectTrigger className="w-full">
        <SelectValue placeholder={t("Bestandsaufnahmen")} />
      </SelectTrigger>
      <SelectContent>
        <SelectGroup>
          {requirementList.items.map((requirement) => (
            <SelectItem key={requirement.id} value={requirement.id}>
              <RequirementDisplayRow requirement={requirement} />
            </SelectItem>
          ))}
        </SelectGroup>
      </SelectContent>
    </Select>
  );
}

export type SelectRequirementButtonComponentProps = {
  calculationId?: string;
  processId?: string;
  selectedRequirementId?: string;
};

export function SelectRequirementButtonComponent({
  calculationId,
  processId,
  selectedRequirementId,
}: SelectRequirementButtonComponentProps) {
  const {
    data: selectedRequirement,
    isLoading: isLoadingSelectedRequirement,
    error: errorSelectedRequirement,
  } = useQpmRequirementGetQuery(
    { id: selectedRequirementId! },
    { skip: !selectedRequirementId },
  );

  if (errorSelectedRequirement) {
    return <RTKQueryErrorAlert error={errorSelectedRequirement} />;
  }

  if (isLoadingSelectedRequirement) {
    return <Loader2 className="animate-spin" />;
  }

  return (
    <SelectDocumentButton selectedDocumentId={selectedRequirement?.id}>
      <CreateFromRequirement
        calculationId={calculationId}
        processId={processId}
      />
    </SelectDocumentButton>
  );
}

export type SelectDocumentButtonProps = {
  selectedDocumentId?: string;
  defaultButtonText?: string;
  children?: ReactNode;
};

export function SelectDocumentButton({
  selectedDocumentId,
  defaultButtonText,
  children,
}: SelectDocumentButtonProps) {
  if (selectedDocumentId && selectedDocumentId !== "") {
    return (
      <Button size="sm" variant="outline" disabled onClick={() => {}}>
        <DocumentNumberById
          className="text-sm"
          documentId={selectedDocumentId}
        />
      </Button>
    );
  }

  return (
    <Dialog>
      <DialogTrigger className="border-input bg-background hover:bg-accent hover:text-accent-foreground h-9 rounded-md border px-3">
        {(defaultButtonText && t(defaultButtonText)) ||
          t("-- nicht gewählt --")}
      </DialogTrigger>
      <DialogTitle title={t("Bestandsaufnahmen")} />
      <DialogContent className="max-h-[80vh] min-w-[80vw] overflow-y-scroll">
        {children}
      </DialogContent>
    </Dialog>
  );
}

type RequirementDisplayRowProps = {
  requirement: RequirementListDTO;
};

function RequirementDisplayRow({ requirement }: RequirementDisplayRowProps) {
  return (
    <div className="grid grid-cols-2">
      <div className="flex items-center">
        <div className="border-r px-2">
          <DocumentNumberById className="text-sm" documentId={requirement.id} />
        </div>
        <div className="border-r px-2">
          <span>{requirement.subject}</span>
        </div>
      </div>
      <div className="flex items-center">
        <div className="px-2">
          <span>{formatDate(requirement.createdAt)}</span>
        </div>
        <div className="px-2">
          <span>{formatTime(requirement.createdAt)}</span>
        </div>
      </div>
    </div>
  );
}
